import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Mail, User, Key, ArrowLeft, CheckCircle, Loader2, Shield, RefreshCw, Phone } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Configure axios with timeout for slow mobile networks
const authApi = axios.create({
  timeout: 30000, // 30 second timeout
});

export function LoginPage({ onAuthenticated }) {
  const [step, setStep] = useState("username"); // username, code, password, reset-request, reset-code
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [firstLogin, setFirstLogin] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const token = localStorage.getItem("vpp_token");
    if (token) {
      authApi.get(`${API}/api/auth/verify-token`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        if (res.data.valid) {
          localStorage.setItem("vpp_user", JSON.stringify(res.data));
          onAuthenticated();
        }
      }).catch(() => {
        localStorage.removeItem("vpp_token");
        localStorage.removeItem("vpp_user");
      });
    }
  }, [onAuthenticated]);

  const [loginMethod, setLoginMethod] = useState("email"); // "sms" or "email"
  
  const handleRequestLogin = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true);
    setError("");

    try {
      const res = await authApi.post(`${API}/api/auth/request-login`, { username: username.trim() });
      if (res.data) {
        setMaskedEmail(res.data.masked_destination || "your phone/email");
        setLoginMethod(res.data.method || "email");
        setDisplayName(res.data.name || username);
        setFirstLogin(res.data.first_login);
        if (res.data.username) {
          setUsername(res.data.username); // Use correct casing from server
        }
        setStep("code");
      } else {
        // Fallback if response format is unexpected but call succeeded
        setStep("code");
        setMaskedEmail("your phone/email");
      }
    } catch (err) {
      console.error("Login request error:", err);
      if (err.response?.status === 404) {
        setError("Username not found. Please check and try again.");
      } else if (err.response?.status === 429) {
        setError("Too many attempts. Please wait a few minutes.");
      } else {
        setError(err.response?.data?.detail || "Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    setError("");

    try {
      const res = await authApi.post(`${API}/api/auth/verify-2fa`, { username, code, remember_me: rememberMe });
      
      if (res.data.first_login) {
        // Use the username from API response (correct casing)
        setUsername(res.data.username);
        setDisplayName(res.data.name || res.data.username);
        setFirstLogin(true);
        setStep("password");
      } else {
        localStorage.setItem("vpp_token", res.data.token);
        localStorage.setItem("vpp_user", JSON.stringify({
          username: res.data.username,
          name: res.data.name
        }));
        onAuthenticated();
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code. Try again.");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) return;
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await authApi.post(`${API}/api/auth/set-password`, {
        new_password: newPassword,
        confirm_password: confirmPassword,
        remember_me: rememberMe
      }, {
        headers: { "X-Username": username }
      });

      localStorage.setItem("vpp_token", res.data.token);
      localStorage.setItem("vpp_user", JSON.stringify({
        username: res.data.username,
        name: res.data.name
      }));
      onAuthenticated();
    } catch (err) {
      console.error("Set password error:", err.response?.data || err.message);
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(detail);
      } else if (err.response?.status === 401) {
        setError("Session expired. Please start over.");
        resetToStart();
      } else if (err.response?.status === 404) {
        setError("User not found. Please start over.");
        resetToStart();
      } else {
        setError("Failed to set password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true);
    setError("");

    try {
      const res = await authApi.post(`${API}/api/auth/login-password`, {
        username: username.trim(),
        password,
        remember_me: rememberMe
      });

      localStorage.setItem("vpp_token", res.data.token);
      localStorage.setItem("vpp_user", JSON.stringify({
        username: res.data.username,
        name: res.data.name
      }));
      onAuthenticated();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.includes("Password not set")) {
        setUsePassword(false);
        setError("Password not set yet. Use 2FA login first.");
      } else {
        setError(detail || "Invalid credentials. Try again.");
      }
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true);
    setError("");

    try {
      const res = await authApi.post(`${API}/api/auth/request-password-reset`, { username: username.trim() });
      if (res.data && res.data.email) {
        setMaskedEmail(res.data.email);
        setStep("reset-code");
      } else {
        // Fallback if response format is unexpected
        setStep("reset-code");
        setMaskedEmail("your email");
      }
    } catch (err) {
      console.error("Password reset request error:", err);
      if (err.response?.status === 404) {
        setError("Username not found. Please check and try again.");
      } else if (err.response?.status === 429) {
        setError("Too many attempts. Please wait a few minutes.");
      } else {
        setError(err.response?.data?.detail || "Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || !newPassword || !confirmPassword) return;
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await authApi.post(`${API}/api/auth/reset-password`, {
        username,
        code,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      localStorage.setItem("vpp_token", res.data.token);
      localStorage.setItem("vpp_user", JSON.stringify({
        username: res.data.username,
        name: res.data.name
      }));
      onAuthenticated();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. Try again.");
      if (err.response?.data?.detail?.includes("expired")) {
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetToStart = () => {
    setStep("username");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setUsePassword(false);
    setPassword("");
  };

  // Username step
  if (step === "username") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="login-page">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#09090b] border border-white/5 p-12 w-full max-w-sm text-center"
        >
          <Shield size={32} className="text-[#3b82f6] mx-auto mb-6" />
          <h2 className="font-heading text-2xl uppercase tracking-wide text-white mb-2">Dashboard</h2>
          <p className="text-zinc-500 font-body text-sm mb-8">
            {usePassword ? "Enter your credentials" : "Enter your username to get a login code"}
          </p>

          {usePassword ? (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  data-testid="login-username-input"
                  className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-body placeholder-zinc-700 w-full transition-colors"
                  autoFocus
                />
              </div>
              <div className="relative">
                <Key size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  data-testid="login-password-input"
                  className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-body placeholder-zinc-700 w-full transition-colors"
                />
              </div>
              {error && <p className="text-red-400 text-sm font-body" data-testid="login-error">{error}</p>}
              <label className="flex items-center gap-2 cursor-pointer justify-start">
                <input 
                  type="checkbox" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 bg-transparent border border-white/20 rounded cursor-pointer accent-[#3b82f6]"
                  data-testid="remember-me-checkbox"
                />
                <span className="text-zinc-500 text-sm font-body">Remember me for 30 days</span>
              </label>
              <button 
                type="submit" 
                disabled={loading || !username.trim() || !password}
                data-testid="login-submit-btn"
                className="flex items-center justify-center gap-2 bg-white text-black font-heading text-sm tracking-[0.15em] uppercase px-8 py-3 hover:bg-zinc-200 transition-colors cursor-pointer border-none w-full disabled:opacity-50"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Login"}
              </button>
              <div className="flex items-center justify-center gap-4">
                <button 
                  type="button"
                  onClick={() => { setUsePassword(false); setError(""); setPassword(""); }}
                  className="text-zinc-600 text-sm hover:text-zinc-400 transition-colors bg-transparent border-none cursor-pointer"
                >
                  Use 2FA login
                </button>
                <span className="text-zinc-800">|</span>
                <button 
                  type="button"
                  onClick={() => { setStep("reset-request"); setError(""); setPassword(""); }}
                  className="text-[#f59e0b] text-sm hover:text-[#f59e0b]/80 transition-colors bg-transparent border-none cursor-pointer"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleRequestLogin} className="space-y-4">
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  data-testid="login-username-input"
                  className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-body placeholder-zinc-700 w-full transition-colors"
                  autoFocus
                />
              </div>
              {error && <p className="text-red-400 text-sm font-body" data-testid="login-error">{error}</p>}
              <label className="flex items-center gap-2 cursor-pointer justify-start">
                <input 
                  type="checkbox" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 bg-transparent border border-white/20 rounded cursor-pointer accent-[#3b82f6]"
                  data-testid="remember-me-checkbox-2fa"
                />
                <span className="text-zinc-500 text-sm font-body">Remember me for 30 days</span>
              </label>
              <button 
                type="submit" 
                disabled={loading || !username.trim()}
                data-testid="login-submit-btn"
                className="flex items-center justify-center gap-2 bg-white text-black font-heading text-sm tracking-[0.15em] uppercase px-8 py-3 hover:bg-zinc-200 transition-colors cursor-pointer border-none w-full disabled:opacity-50"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Continue"}
              </button>
              <div className="flex items-center justify-center gap-4">
                <button 
                  type="button"
                  onClick={() => { setUsePassword(true); setError(""); }}
                  className="text-zinc-600 text-sm hover:text-zinc-400 transition-colors bg-transparent border-none cursor-pointer"
                >
                  Login with password
                </button>
                <span className="text-zinc-800">|</span>
                <button 
                  type="button"
                  onClick={() => { setStep("reset-request"); setError(""); }}
                  className="text-[#f59e0b] text-sm hover:text-[#f59e0b]/80 transition-colors bg-transparent border-none cursor-pointer"
                >
                  Forgot Password?
                </button>
              </div>
              
              {/* Google Sign-In Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#09090b] px-3 text-zinc-600 font-body">Or</span>
                </div>
              </div>
              
              {/* Google Sign-In Button */}
              <button
                type="button"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + '/dashboard';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                data-testid="google-signin-btn"
                className="flex items-center justify-center gap-3 w-full bg-white text-black font-body text-sm px-8 py-3 hover:bg-zinc-100 transition-colors cursor-pointer border-none"
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-white/5">
            <p className="text-zinc-600 text-xs">
              Authorized personnel only
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // 2FA Code step
  if (step === "code") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="login-2fa-page">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#09090b] border border-white/5 p-12 w-full max-w-sm text-center"
        >
          {loginMethod === "sms" ? (
            <Phone size={32} className="text-[#22c55e] mx-auto mb-6" />
          ) : (
            <Mail size={32} className="text-[#22c55e] mx-auto mb-6" />
          )}
          <h2 className="font-heading text-2xl uppercase tracking-wide text-white mb-2">
            {loginMethod === "sms" ? "Check Your Phone" : "Check Your Email"}
          </h2>
          <p className="text-zinc-500 font-body text-sm mb-2">
            Hey {displayName}, we sent a 6-digit code {loginMethod === "sms" ? "via SMS to" : "to"}:
          </p>
          <p className="text-[#3b82f6] font-mono text-sm mb-8">
            {loginMethod === "sms" && <span className="text-zinc-500 mr-1">📱</span>}
            {maskedEmail}
          </p>

          <form onSubmit={handleVerify2FA} className="space-y-4">
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                data-testid="login-code-input"
                className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-mono text-xl text-center tracking-[0.5em] placeholder-zinc-700 w-full transition-colors"
                autoFocus
                maxLength={6}
              />
            </div>
            {error && <p className="text-red-400 text-sm font-body" data-testid="login-error">{error}</p>}
            <button 
              type="submit" 
              disabled={loading || code.length !== 6}
              data-testid="login-verify-btn"
              className="flex items-center justify-center gap-2 bg-[#22c55e] text-black font-heading text-sm tracking-[0.15em] uppercase px-8 py-3 hover:bg-[#22c55e]/80 transition-colors cursor-pointer border-none w-full disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Verify Code"}
            </button>
          </form>

          <div className="flex items-center justify-center gap-4 mt-6">
            <button 
              onClick={resetToStart}
              className="flex items-center gap-1 text-zinc-600 text-sm hover:text-zinc-400 transition-colors bg-transparent border-none cursor-pointer"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <span className="text-zinc-800">|</span>
            <button 
              onClick={handleRequestLogin}
              disabled={loading}
              className="text-zinc-600 text-sm hover:text-zinc-400 transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
            >
              Resend Code
            </button>
          </div>

          <p className="text-zinc-700 text-xs mt-6">
            Code expires in 10 minutes
          </p>
        </motion.div>
      </div>
    );
  }

  // Set Password step (first login)
  if (step === "password") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="login-password-page">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#09090b] border border-white/5 p-12 w-full max-w-sm text-center"
        >
          <CheckCircle size={32} className="text-[#22c55e] mx-auto mb-6" />
          <h2 className="font-heading text-2xl uppercase tracking-wide text-white mb-2">Welcome, {displayName}!</h2>
          <p className="text-zinc-500 font-body text-sm mb-8">
            Set a password to complete your account setup
          </p>

          <form onSubmit={handleSetPassword} className="space-y-4">
            <div className="relative">
              <Key size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New Password"
                data-testid="login-new-password-input"
                className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-body placeholder-zinc-700 w-full transition-colors"
                autoFocus
              />
            </div>
            <div className="relative">
              <Key size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                data-testid="login-confirm-password-input"
                className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-body placeholder-zinc-700 w-full transition-colors"
              />
            </div>
            {error && <p className="text-red-400 text-sm font-body" data-testid="login-error">{error}</p>}
            <p className="text-zinc-600 text-xs text-left">
              Password must be at least 8 characters
            </p>
            <button 
              type="submit" 
              disabled={loading || !newPassword || !confirmPassword}
              data-testid="login-set-password-btn"
              className="flex items-center justify-center gap-2 bg-[#3b82f6] text-white font-heading text-sm tracking-[0.15em] uppercase px-8 py-3 hover:bg-[#3b82f6]/80 transition-colors cursor-pointer border-none w-full disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Set Password & Login"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Password Reset Request step
  if (step === "reset-request") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="login-reset-request-page">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#09090b] border border-white/5 p-12 w-full max-w-sm text-center"
        >
          <RefreshCw size={32} className="text-[#f59e0b] mx-auto mb-6" />
          <h2 className="font-heading text-2xl uppercase tracking-wide text-white mb-2">Reset Password</h2>
          <p className="text-zinc-500 font-body text-sm mb-8">
            Enter your username to receive a reset code
          </p>

          <form onSubmit={handleRequestPasswordReset} className="space-y-4">
            <div className="relative">
              <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                data-testid="reset-username-input"
                className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-body placeholder-zinc-700 w-full transition-colors"
                autoFocus
              />
            </div>
            {error && <p className="text-red-400 text-sm font-body" data-testid="reset-error">{error}</p>}
            <button 
              type="submit" 
              disabled={loading || !username.trim()}
              data-testid="reset-submit-btn"
              className="flex items-center justify-center gap-2 bg-[#f59e0b] text-black font-heading text-sm tracking-[0.15em] uppercase px-8 py-3 hover:bg-[#f59e0b]/80 transition-colors cursor-pointer border-none w-full disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Send Reset Code"}
            </button>
          </form>

          <button 
            onClick={resetToStart}
            className="flex items-center justify-center gap-1 mt-6 text-zinc-600 text-sm hover:text-zinc-400 transition-colors bg-transparent border-none cursor-pointer mx-auto"
          >
            <ArrowLeft size={14} /> Back to Login
          </button>
        </motion.div>
      </div>
    );
  }

  // Password Reset Code + New Password step
  if (step === "reset-code") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="login-reset-code-page">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#09090b] border border-white/5 p-12 w-full max-w-sm text-center"
        >
          <Mail size={32} className="text-[#f59e0b] mx-auto mb-6" />
          <h2 className="font-heading text-2xl uppercase tracking-wide text-white mb-2">Check Your Email</h2>
          <p className="text-zinc-500 font-body text-sm mb-2">
            We sent a reset code to:
          </p>
          <p className="text-[#f59e0b] font-mono text-sm mb-8">{maskedEmail}</p>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                data-testid="reset-code-input"
                className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-mono text-xl text-center tracking-[0.5em] placeholder-zinc-700 w-full transition-colors"
                autoFocus
                maxLength={6}
              />
            </div>
            <div className="relative">
              <Key size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New Password"
                data-testid="reset-new-password-input"
                className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-body placeholder-zinc-700 w-full transition-colors"
              />
            </div>
            <div className="relative">
              <Key size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                data-testid="reset-confirm-password-input"
                className="bg-transparent border-b border-white/10 focus:border-white outline-none p-4 pl-11 text-white font-body placeholder-zinc-700 w-full transition-colors"
              />
            </div>
            {error && <p className="text-red-400 text-sm font-body" data-testid="reset-error">{error}</p>}
            <p className="text-zinc-600 text-xs text-left">
              Password must be at least 8 characters
            </p>
            <button 
              type="submit" 
              disabled={loading || code.length !== 6 || !newPassword || !confirmPassword}
              data-testid="reset-password-btn"
              className="flex items-center justify-center gap-2 bg-[#f59e0b] text-black font-heading text-sm tracking-[0.15em] uppercase px-8 py-3 hover:bg-[#f59e0b]/80 transition-colors cursor-pointer border-none w-full disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Reset Password & Login"}
            </button>
          </form>

          <div className="flex items-center justify-center gap-4 mt-6">
            <button 
              onClick={resetToStart}
              className="flex items-center gap-1 text-zinc-600 text-sm hover:text-zinc-400 transition-colors bg-transparent border-none cursor-pointer"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <span className="text-zinc-800">|</span>
            <button 
              onClick={handleRequestPasswordReset}
              disabled={loading}
              className="text-zinc-600 text-sm hover:text-zinc-400 transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
            >
              Resend Code
            </button>
          </div>

          <p className="text-zinc-700 text-xs mt-6">
            Code expires in 10 minutes
          </p>
        </motion.div>
      </div>
    );
  }

  return null;
}

export default LoginPage;
